import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import FeedbackDialog from './FeedbackDialog';
import { getFeedbackList, saveFeedback } from '../utilities/api';
import { exportStyledExcel, buildReportFilterLines } from '../utilities/excelExport';
import './Feedback.css';

const emptyFeedback = {
    feedback_id: null,
    feedback_date: new Date(),
    roomno: '',
    bedno: '',
    customername: '',
    phone_number: '',
    email_id: '',
    Food_quality: '',
    cleanliness: '',
    staff_behaviour: '',
    affordabality: '',
    food_option_available: '',
    services: '',
    menusoption: '',
    expericenceoption: '',
    comments: '',
    appreciate: ''
};

const formatFeedbackDate = (value) => (value && moment(value, 'YYYY-MM-DD', true).isValid() ? moment(value).format('DD-MM-YYYY') : '-');

const ratingSeverity = (value) => {
    switch (value) {
        case 'Excellent':
        case 'Good':
            return 'success';
        case 'Average':
            return 'warning';
        case 'Satisfactory':
            return 'info';
        case 'Not Satisfactory':
            return 'danger';
        default:
            return null;
    }
};

const RatingTag = ({ value }) => (value ? <Tag value={value} severity={ratingSeverity(value)} /> : <span className="text-color-secondary">-</span>);

const Feedback = () => {
    const toast = useRef(null);
    const dt = useRef(null);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [selectedFeedback, setSelectedFeedback] = useState(emptyFeedback);
    const [showDialog, setShowDialog] = useState(false);

    const loadData = async (params = {}) => {
        setLoading(true);
        try {
            const res = await getFeedbackList(params);
            setData(res?.feedback || []);
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load feedback data', life: 4000 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentDateFilter = () => ({
        date_from: fromDate ? moment(fromDate).format('YYYY-MM-DD') : '',
        date_to: toDate ? moment(toDate).format('YYYY-MM-DD') : ''
    });

    const handleApplyFilters = () => {
        loadData(currentDateFilter());
    };

    const handleReset = () => {
        setFromDate(null);
        setToDate(null);
        loadData();
    };

    const openNew = () => {
        setSelectedFeedback({ ...emptyFeedback, feedback_date: new Date() });
        setShowDialog(true);
    };

    const hideDialog = () => {
        setShowDialog(false);
        setSelectedFeedback(emptyFeedback);
    };

    const handleSaveRequest = (feedbackPayload) => {
        confirmDialog({
            message: feedbackPayload.feedback_id ? 'Are you sure you want to update this feedback record?' : 'Are you sure you want to save this feedback?',
            header: feedbackPayload.feedback_id ? 'Confirm Update' : 'Confirm Save',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Save',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: () => handleSave(feedbackPayload)
        });
    };

    const handleSave = async (feedbackPayload) => {
        setSaving(true);
        try {
            const res = await saveFeedback(feedbackPayload);
            if (res?.status) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: res.message || 'Feedback saved successfully', life: 3000 });
                hideDialog();
                loadData(currentDateFilter());
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res?.message || 'Save failed', life: 5000 });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Connection Error', detail: err.message, life: 5000 });
        } finally {
            setSaving(false);
        }
    };

    const filteredData = useMemo(() => {
        const rows = dt.current?.filteredValue;
        return Array.isArray(rows) ? rows : data;
    }, [data, globalFilterValue]);

    const ratingColumns = [
        { header: 'Quality of food', getValue: (r) => r.Food_quality || '-' },
        { header: 'Cleanliness', getValue: (r) => r.cleanliness || '-' },
        { header: 'Staff behaviour', getValue: (r) => r.staff_behaviour || '-' },
        { header: 'Speed of service', getValue: (r) => r.services || '-' },
        { header: 'Food options', getValue: (r) => r.food_option_available || '-' },
        { header: 'Menu variety', getValue: (r) => r.menusoption || '-' },
        { header: 'Affordability', getValue: (r) => r.affordabality || '-' },
        { header: 'Overall experience', getValue: (r) => r.expericenceoption || '-' }
    ];

    const exportExcel = async () => {
        if (!data.length) {
            toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'No data to export' });
            return;
        }
        const columns = [
            { header: 'Date', getValue: (r) => formatFeedbackDate(r.feedback_date) },
            { header: 'Room No', getValue: (r) => r.roomno || '-' },
            { header: 'Bed No', getValue: (r) => r.bedno || '-' },
            { header: 'Patient Name', getValue: (r) => r.customername || '-' },
            { header: 'Mobile No', getValue: (r) => r.phone_number || '-' },
            { header: 'Email ID', getValue: (r) => r.email_id || '-' },
            ...ratingColumns,
            { header: 'Comments', getValue: (r) => r.comments || '-' },
            { header: 'Appreciation', getValue: (r) => r.appreciate || '-' }
        ];
        await exportStyledExcel({
            fileName: `Feedback_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Feedback',
            title: 'Feedback Report',
            filterLines: buildReportFilterLines({ fromDate, toDate }),
            columns,
            rows: filteredData,
            showFooterTotal: false
        });
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Data exported successfully' });
    };

    const exportPDF = () => {
        if (!filteredData.length) {
            toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'No data to export' });
            return;
        }
        const doc = new jsPDF('l', 'mm', 'a4');
        doc.setFontSize(16);
        doc.text('Feedback Report', 14, 15);
        doc.setFontSize(10);
        let yPos = 22;
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, yPos);
            yPos += 5;
        }
        const tableBody = filteredData.map((r) => [
            formatFeedbackDate(r.feedback_date),
            r.roomno || '-',
            r.bedno || '-',
            r.customername || '-',
            r.phone_number || '-',
            r.Food_quality || '-',
            r.cleanliness || '-',
            r.staff_behaviour || '-',
            r.services || '-',
            r.food_option_available || '-',
            r.menusoption || '-',
            r.affordabality || '-',
            r.expericenceoption || '-'
        ]);
        autoTable(doc, {
            startY: yPos + 3,
            head: [['Date', 'Room', 'Bed', 'Patient', 'Mobile', 'Food Qlty', 'Clean', 'Staff', 'Speed', 'Options', 'Variety', 'Afford.', 'Overall']],
            body: tableBody,
            styles: { fontSize: 7 }
        });
        doc.save(`Feedback_${moment().format('YYYY-MM-DD')}.pdf`);
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'PDF exported successfully' });
    };

    return (
        <div className="fb-page">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Feedback
                </h2>
                <p className="text-color-secondary">Patient food-court feedback — view, filter and manage feedback records</p>
            </div>

            <Card className="mb-4 fb-filter-card">
                <div className="flex flex-wrap gap-4 align-items-end fb-filter-row">
                    <div className="flex flex-column" style={{ flex: '1 1 180px', minWidth: '160px' }}>
                        <label className="mb-2 font-medium text-sm">From Date</label>
                        <Calendar value={fromDate} onChange={(e) => setFromDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="From Date" className="w-full" showButtonBar />
                    </div>
                    <div className="flex flex-column" style={{ flex: '1 1 180px', minWidth: '160px' }}>
                        <label className="mb-2 font-medium text-sm">To Date</label>
                        <Calendar value={toDate} onChange={(e) => setToDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="To Date" minDate={fromDate} className="w-full" showButtonBar />
                    </div>
                    <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                        <Button label="Apply" icon="pi pi-check" className="p-button-primary" onClick={handleApplyFilters} loading={loading} />
                        <Button icon="pi pi-refresh" className="p-button-secondary" onClick={handleReset} tooltip="Reset" />
                    </div>
                </div>
            </Card>

            <Card className="fb-table-card">
                <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <h3 className="text-xl font-semibold m-0">Feedback Records</h3>
                        <Tag value={`${filteredData.length} of ${data.length} records`} severity="info" />
                    </div>

                    <div className="flex flex-wrap gap-2 align-items-center">
                        <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                            <i className="pi pi-search" />
                            <InputText
                                value={globalFilterValue}
                                onChange={(e) => {
                                    setGlobalFilterValue(e.target.value);
                                    setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                                }}
                                placeholder="Search feedback..."
                                className="w-full"
                            />
                        </span>
                        <Button label="Add Feedback" icon="pi pi-plus" className="p-button-primary" onClick={openNew} />
                        <Button label="Excel" icon="pi pi-file-excel" className="p-button-success fb-excel-btn" onClick={exportExcel} disabled={!data.length} />
                        <Button label="PDF" icon="pi pi-file-pdf" className="p-button-danger" onClick={exportPDF} disabled={!data.length} />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <DataTable
                        ref={dt}
                        value={data}
                        filters={filters}
                        globalFilterFields={['roomno', 'bedno', 'customername', 'phone_number', 'email_id']}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                        showGridlines
                        scrollable
                        tableStyle={{ minWidth: '95rem' }}
                        className="p-datatable-sm fb-datatable"
                        emptyMessage="No feedback records found"
                    >
                        <Column field="feedback_date" header="Date" sortable style={{ minWidth: '110px' }} body={(r) => formatFeedbackDate(r.feedback_date)} />
                        <Column field="roomno" header="Room No" sortable style={{ minWidth: '90px' }} />
                        <Column field="bedno" header="Bed No" sortable style={{ minWidth: '90px' }} />
                        <Column field="customername" header="Patient Name" sortable style={{ minWidth: '150px' }} />
                        <Column field="phone_number" header="Mobile No" style={{ minWidth: '110px' }} />
                        <Column field="email_id" header="Email ID" style={{ minWidth: '140px' }} />
                        <Column field="Food_quality" header="Quality of Food" body={(r) => <RatingTag value={r.Food_quality} />} style={{ minWidth: '140px' }} />
                        <Column field="cleanliness" header="Cleanliness" body={(r) => <RatingTag value={r.cleanliness} />} style={{ minWidth: '130px' }} />
                        <Column field="staff_behaviour" header="Staff Behaviour" body={(r) => <RatingTag value={r.staff_behaviour} />} style={{ minWidth: '140px' }} />
                        <Column field="services" header="Speed of Service" body={(r) => <RatingTag value={r.services} />} style={{ minWidth: '150px' }} />
                        <Column field="food_option_available" header="Food Options" body={(r) => <RatingTag value={r.food_option_available} />} style={{ minWidth: '130px' }} />
                        <Column field="menusoption" header="Menu Variety" body={(r) => <RatingTag value={r.menusoption} />} style={{ minWidth: '130px' }} />
                        <Column field="affordabality" header="Affordability" body={(r) => <RatingTag value={r.affordabality} />} style={{ minWidth: '130px' }} />
                        <Column field="expericenceoption" header="Overall Experience" body={(r) => <RatingTag value={r.expericenceoption} />} style={{ minWidth: '150px' }} />
                    </DataTable>
                )}
            </Card>

            <FeedbackDialog visible={showDialog} onHide={hideDialog} feedbackData={selectedFeedback} onSave={handleSaveRequest} saving={saving} />
        </div>
    );
};

export default Feedback;
